import React, { useState, useEffect } from 'react';
import { Modal, Button } from '../../components/ds';
import api from '../../api/client';

export default function AddCompanyModal({ accountId, onClose, onAdded }) {
  const [companies, setCompanies] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchAvailable = async () => {
      try {
        const [allCompanies, accountCompanies] = await Promise.all([
          api.getCompanies(),
          api.getAccountCompanies(accountId),
        ]);

        const associatedIds = new Set(
          (accountCompanies || []).map((c) => c.company_id)
        );

        const available = (allCompanies || []).filter(
          (c) => !associatedIds.has(c.uuid)
        );

        setCompanies(available);
      } catch (err) {
        setError('Failed to load companies: ' + err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchAvailable();
  }, [accountId]);

  const selectedName = companies.find((c) => c.uuid === selected)?.company_name || '';

  const handleSubmit = async () => {
    if (!selected || !selectedName) return;
    setSubmitting(true);
    setError('');

    try {
      await api.addAccountCompany(accountId, {
        company_id: selected,
        company_name: selectedName,
      });
      onAdded();
    } catch (err) {
      setError('Failed to add company: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal title="Add Company" onClose={onClose} footer={
      <>
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button variant="primary" onClick={handleSubmit} disabled={!selected || submitting}>
          {submitting ? 'Adding...' : 'Confirm'}
        </Button>
      </>
    }>
      {loading ? (
        <div className="loading">Loading companies...</div>
      ) : companies.length === 0 ? (
        <p style={{ color: 'var(--text-secondary)', textAlign: 'center', padding: 20 }}>
          No available companies to add
        </p>
      ) : (
        <>
          <p style={{ marginBottom: 12, color: 'var(--text-secondary)', fontSize: 14 }}>
            Select a company to associate with this account:
          </p>
          <div style={{ maxHeight: 300, overflowY: 'auto' }}>
            {companies.map((company) => (
              <label
                key={company.uuid}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '10px 12px',
                  borderRadius: 'var(--radius-md)',
                  cursor: 'pointer',
                  border: selected === company.uuid
                    ? '2px solid var(--primary-orange)'
                    : '1px solid var(--border-color)',
                  marginBottom: 8,
                  backgroundColor: selected === company.uuid ? 'var(--bg-secondary)' : 'transparent',
                }}
              >
                <input
                  type="radio"
                  name="company"
                  checked={selected === company.uuid}
                  onChange={() => setSelected(company.uuid)}
                />
                <span style={{ fontWeight: 500 }}>{company.company_name}</span>
              </label>
            ))}
          </div>
        </>
      )}

      {error && <div className="error-text">{error}</div>}
    </Modal>
  );
}
